/**
 * Billing API client
 * Connects frontend to Billing service backend
 */

import apiClient from './auth';

export interface Order {
  order_uuid: string;
  institution_uuid: string;
  batch_uuid?: string;
  submission_count: number;
  calculated_fee: number;
  payment_status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  invoice_path?: string;
  created_at: string;
  due_date: string;
  paid_at?: string;
  transactions?: Transaction[];
  invoice_url?: string;
}

export interface Transaction {
  transaction_uuid: string;
  amount: number;
  payment_method: string;
  transaction_date: string;
  mpesa_reference?: string;
  bank_reference?: string;
  status: string;
}

export interface OrderListResponse {
  orders: Order[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Get order details
 */
export async function getOrder(orderUuid: string): Promise<Order> {
  const response = await apiClient.get<{ order: Order }>(`/billing/orders/${orderUuid}`);
  return response.data.order;
}

/**
 * List orders for institution
 */
export async function listOrders(
  institutionUuid: string,
  page: number = 1,
  limit: number = 20
): Promise<OrderListResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  const response = await apiClient.get<OrderListResponse>(
    `/billing/institutions/${institutionUuid}/orders?${params.toString()}`
  );
  return response.data;
}

/**
 * Generate PDF invoice
 */
/**
 * Redeem an innovator assessment voucher (grants same access as Paystack fee).
 */
export interface FilingDocPrepSubmissionRow {
  submission_uuid: string;
  title: string;
  submission_date?: string;
  status: string;
  feedback_approval_status?: string;
}

export interface FilingDocPrepEligibility {
  currency: string;
  amount_major: number;
  amount_paystack_minor: number | null;
  submissions: FilingDocPrepSubmissionRow[];
  /** Server has PAYSTACK_FILING_DOC_PREP_PAGE_URL (static Paystack Payment Page, same pattern as assessment). */
  payment_page_configured: boolean;
}

/**
 * Submissions eligible for Paystack filing-documentation prep billing (individual innovators).
 */
export async function getFilingDocPrepEligibility(): Promise<FilingDocPrepEligibility> {
  const response = await apiClient.get<FilingDocPrepEligibility>('/billing/paystack/filing-doc-prep/eligibility');
  return response.data;
}

/**
 * Registers pending checkout for webhook matching, returns Paystack Payment Page URL (email prefilled).
 * Open payment_page_url in a new tab; poll GET /innovations/:uuid until filing_documentation_prep.billing_cleared_at is set.
 */
export async function beginFilingDocPrepCheckout(submissionUuid: string): Promise<{
  message: string;
  payment_page_url: string;
  pending_expires_in_minutes?: number;
}> {
  const response = await apiClient.post<{
    message: string;
    payment_page_url: string;
    pending_expires_in_minutes?: number;
  }>('/billing/paystack/filing-doc-prep/begin-checkout', { submission_uuid: submissionUuid });
  return response.data;
}

export async function redeemInnovatorVoucher(code: string): Promise<{
  message: string;
  innovator_assessment_fee_paid_at: string;
}> {
  const response = await apiClient.post<{
    message: string;
    innovator_assessment_fee_paid_at: string;
  }>('/billing/vouchers/redeem', { code: code.trim() });
  return response.data;
}

/** Applies a 40% assessment discount voucher and returns the discounted Paystack page URL (KES 6,000). */
export async function redeemAssessmentDiscountVoucher(code: string): Promise<{
  message: string;
  payment_page_url: string;
  pending_expires_in_minutes?: number;
}> {
  const response = await apiClient.post<{
    message: string;
    payment_page_url: string;
    pending_expires_in_minutes?: number;
  }>('/billing/vouchers/redeem-assessment-discount', { code: code.trim() });
  return response.data;
}

/** Clears filing-doc prep billing on the chosen submission (same outcome as Paystack for that row). */
export async function redeemFilingDocPrepVoucher(
  code: string,
  submissionUuid: string
): Promise<{
  message: string;
  submission_uuid: string;
  filing_doc_prep_billing_cleared_at: string;
}> {
  const response = await apiClient.post<{
    message: string;
    submission_uuid: string;
    filing_doc_prep_billing_cleared_at: string;
  }>('/billing/vouchers/redeem-filing-doc-prep', {
    code: code.trim(),
    submission_uuid: submissionUuid,
  });
  return response.data;
}

export async function generateInvoice(orderUuid: string): Promise<{
  message: string;
  invoice: {
    order_uuid: string;
    invoice_path: string;
    file_size: number;
    download_url: string;
  };
}> {
  const response = await apiClient.post(`/billing/orders/${orderUuid}/invoice`);
  return response.data;
}
